import _ from "lodash";
import { TriangleAlert } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ArrangedUseLimitData } from "../../../models/featureUseLimits/FeatureUseLimitsData";
import DropdownSelect from "../../FormElement/DropdownSelect";
import InputBox from "../../FormElement/InputBox";
import { ToggleSwitch } from "../ToggleSwitch";

import "./styles.scss";

export const ActionTypes = {
  EVERY_DAY: "EVERY_DAY",
  EVERY_WEEK: "EVERY_WEEK",
  EVERY_MONTH: "EVERY_MONTH",
  EVERY_YEAR: "EVERY_YEAR",
};

export interface IBulkToggleSignal {
  can_use: boolean;
  token: number;
}

export interface IEditUseLimitItemProps {
  arrangedUseLimit: ArrangedUseLimitData;
  mode: string;
  bulkToggle?: IBulkToggleSignal | null;
  onChange: (needUpdate: boolean, item: any) => void;
}

const EditUseLimitItem: React.FunctionComponent<IEditUseLimitItemProps> = ({
  arrangedUseLimit,
  mode,
  bulkToggle,
  onChange,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`functionManagementPage.modalEditUseLimit.${key}`);
  const tOptional = (key: string) =>
    _t(`functionManagementPage.modalEditUseLimit.${key}`, {
      defaultValue: "",
    });

  const [updateUseLimitItem, setUpdateUseLimitItem] =
    useState<ArrangedUseLimitData>(arrangedUseLimit);

  const [savedUseLimits, setSavedUseLimits] = useState(arrangedUseLimit); // For saving last state of updated use limits, before set to default
  const [defaultUseLimits] = useState(arrangedUseLimit); // For saving default use limits

  useEffect(() => {
    if (mode === "reset") {
      setSavedUseLimits(_.cloneDeep(updateUseLimitItem));

      const cloneUpdateUseLimitItem = _.cloneDeep(updateUseLimitItem);
      cloneUpdateUseLimitItem.can_use =
        defaultUseLimits.feature.default_can_use;
      cloneUpdateUseLimitItem.can_use_times =
        defaultUseLimits.feature.default_use_limit;
      cloneUpdateUseLimitItem.use_limit_cycle =
        defaultUseLimits.feature.use_limit_cycle;

      onChange(
        arrangedUseLimit.can_use_times !==
          cloneUpdateUseLimitItem.can_use_times ||
          arrangedUseLimit.can_use !== cloneUpdateUseLimitItem.can_use ||
          arrangedUseLimit.use_limit_cycle !==
            cloneUpdateUseLimitItem.use_limit_cycle,
        cloneUpdateUseLimitItem
      );
      setUpdateUseLimitItem(cloneUpdateUseLimitItem);
    } else {
      onChange(
        arrangedUseLimit.can_use_times !== savedUseLimits.can_use_times ||
          arrangedUseLimit.can_use !== savedUseLimits.can_use ||
          arrangedUseLimit.use_limit_cycle !== savedUseLimits.use_limit_cycle,
        savedUseLimits
      );
      setUpdateUseLimitItem(_.cloneDeep(savedUseLimits));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (!bulkToggle || mode === "reset") {
      return;
    }

    const updatedItem = {
      ...updateUseLimitItem,
      can_use: bulkToggle.can_use,
    };
    setUpdateUseLimitItem(updatedItem);

    onChange(arrangedUseLimit.can_use !== bulkToggle.can_use, updatedItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkToggle?.token]);

  const description = tOptional(
    `descriptions.${arrangedUseLimit.feature.name}`
  );
  const warning = tOptional(`warnings.${arrangedUseLimit.feature.name}`);
  const isEnabled = updateUseLimitItem.can_use;
  const isDisabled = mode === "reset";

  return (
    <div
      className={`edit-feature-item-wrapper ${
        isEnabled ? "is-enabled" : ""
      } ${isDisabled ? "is-locked" : ""}`}
      key={arrangedUseLimit.feature.name}
    >
      <div className="edit-feature-item">
        <div className="feature-copy">
          <div className="feature-name">{t(arrangedUseLimit.feature.name)}</div>
          {description && <div className="feature-description">{description}</div>}
          {warning && isEnabled && (
            <div className="feature-warning">
              <TriangleAlert size={14} strokeWidth={2} />
              <span>{warning}</span>
            </div>
          )}
        </div>
        <ToggleSwitch
          checked={!!isEnabled}
          disabled={isDisabled}
          ariaLabel={t(arrangedUseLimit.feature.name)}
          onChange={(checked) => {
            const updatedItem = {
              ...updateUseLimitItem,
              can_use: checked,
            };
            setUpdateUseLimitItem(updatedItem);

            onChange(arrangedUseLimit.can_use !== checked, updatedItem);
          }}
        />
      </div>
      {arrangedUseLimit.feature.is_metered && (
        <div className="edit-feature-period">
          <DropdownSelect
            isTranslation={true}
            translationKey="functionManagementPage.modalEditUseLimit.actionTypes"
            value={`EVERY_${updateUseLimitItem.use_limit_cycle.toUpperCase()}`}
            items={Object.values(ActionTypes).map((item) => item.toString())}
            isDisabled={!updateUseLimitItem.can_use || mode === "reset"}
            onChange={(value) => {
              const updatedItem = {
                ...updateUseLimitItem,
                use_limit_cycle: value.split("_")[1].toLowerCase(),
              };

              setUpdateUseLimitItem(updatedItem);

              onChange(
                arrangedUseLimit.use_limit_cycle !==
                  value.split("_")[1].toLowerCase(),
                updatedItem
              );
            }}
          />
          <InputBox
            classNameContainer="feature-input-times"
            value={`${updateUseLimitItem.can_use_times || 0}`}
            placeholder={t("period")}
            isDisabled={!updateUseLimitItem.can_use || mode === "reset"}
            onChange={(value) => {
              const updatedItem = {
                ...updateUseLimitItem,
                can_use_times: Number(value),
              };

              setUpdateUseLimitItem(updatedItem);

              onChange(
                arrangedUseLimit.can_use_times !== Number(value),
                updatedItem
              );
            }}
          />
          <div className="times">{t("times")}</div>
        </div>
      )}
    </div>
  );
};

export default EditUseLimitItem;
